import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { CompanyAsset } from "@/context/CompanyContext";

// ── LOA template data shapes ──────────────────────────────────────────
//
// The 4 LOA PDF templates share a uniform data shape. Historically each
// pulled from the report-builder ``ReportConfig.config``; the artefact
// preview route now also feeds them this shape directly via
// ``dataOverride``. Kept type-aligned with that legacy ``config.config``
// so existing PDF render paths keep working unchanged.

export interface LoaPolicy {
  type: string;
  number: string;
}

export interface LoaTemplateData {
  client: {
    name: string;
    dob: string;
    ni: string;
    address: string[];
  };
  provider: {
    name: string;
    address: string[];
  };
  policies: LoaPolicy[];
  adviser: { name: string };
  company: {
    email: string;
    fax: string;
    address: string[];
    phone: string;
    regulation: string;
    registration: string;
    fcaNumber?: string;
  };
  asAt?: string;
}

/** Wraps an ``LoaTemplateData`` to match the legacy ``ReportConfig`` shape. */
export interface LoaTemplatePdfConfig {
  config: LoaTemplateData;
}

// Define the template interface
export interface PDFTemplateProps {
  companyAssets: Record<string, CompanyAsset>;
  landscape?: boolean;
  configId?: string;
  /**
   * When supplied, the template renders against this data instead of
   * pulling from ``usePDFConfig``. Used by the artefact preview route
   * (LOA components only — other templates ignore this).
   */
  dataOverride?: LoaTemplatePdfConfig;
}

// Define template registry types
export type TemplateName =
  | "test"
  | "letter-of-authority"
  | "pension-letter-of-authority"
  | "db-pension-letter-of-authority"
  | "investment-letter-of-authority"
  | "protection-letter-of-authority"
  | "planner-summary"
  | "advisor-client-report"
  | "scenario-tracker"
  | "report-builder";
export type TemplateVersion = "v1" | "latest";

// Template registry with lazy-loaded templates
export const templateRegistry: Record<
  TemplateName,
  LazyExoticComponent<ComponentType<PDFTemplateProps>>
> = {
  test: lazy(() => import("./TestTemplate")),
  "letter-of-authority": lazy(() => import("./LetterOfAuthority")),
  "pension-letter-of-authority": lazy(() => import("./LetterOfAuthority")),
  "db-pension-letter-of-authority": lazy(
    () => import("./DBPensionLetterOfAuthority")
  ),
  "investment-letter-of-authority": lazy(
    () => import("./InvestmentLetterOfAuthority")
  ),
  "protection-letter-of-authority": lazy(
    () => import("./ProtectionLetterOfAuthority")
  ),
  "planner-summary": lazy(() => import("./PlannerSummary")),
  "advisor-client-report": lazy(() => import("./AdvisorClientReport")),
  "scenario-tracker": lazy(() => import("./ScenarioTracker")),
  "report-builder": lazy(() => import("./ReportBuilderTemplate")),
};

// Template version registry for backward compatibility
export const templateVersionRegistry: Record<
  TemplateName,
  Partial<
    Record<
      TemplateVersion,
      LazyExoticComponent<ComponentType<PDFTemplateProps>>
    >
  >
> = {
  test: {
    v1: lazy(() => import("./TestTemplate")),
    latest: lazy(() => import("./TestTemplate")),
  },
  "letter-of-authority": {
    v1: lazy(() => import("./LetterOfAuthority")),
    latest: lazy(() => import("./LetterOfAuthority")),
  },
  "pension-letter-of-authority": {
    v1: lazy(() => import("./LetterOfAuthority")),
    latest: lazy(() => import("./LetterOfAuthority")),
  },
  "db-pension-letter-of-authority": {
    v1: lazy(() => import("./DBPensionLetterOfAuthority")),
    latest: lazy(() => import("./DBPensionLetterOfAuthority")),
  },
  "investment-letter-of-authority": {
    v1: lazy(() => import("./InvestmentLetterOfAuthority")),
    latest: lazy(() => import("./InvestmentLetterOfAuthority")),
  },
  "protection-letter-of-authority": {
    v1: lazy(() => import("./ProtectionLetterOfAuthority")),
    latest: lazy(() => import("./ProtectionLetterOfAuthority")),
  },
  "planner-summary": {
    v1: lazy(() => import("./PlannerSummary")),
    latest: lazy(() => import("./PlannerSummary")),
  },
  "advisor-client-report": {
    v1: lazy(() => import("./AdvisorClientReport")),
    latest: lazy(() => import("./AdvisorClientReport")),
  },
  "scenario-tracker": {
    v1: lazy(() => import("./ScenarioTracker")),
    latest: lazy(() => import("./ScenarioTracker")),
  },
  "report-builder": {
    v1: lazy(() => import("./ReportBuilderTemplate")),
    latest: lazy(() => import("./ReportBuilderTemplate")),
  },
};

/**
 * Get a template component by name and optional version
 * @param templateName The name of the template
 * @param version Optional version of the template
 * @returns The template component or null if not found
 */
export function getTemplate(templateName: string, version?: string) {
  if (!templateName) {
    return null;
  }

  const name = templateName as TemplateName;

  // If version is specified, try to get that specific version
  if (version && templateVersionRegistry[name]) {
    const versionKey = version as TemplateVersion;
    const versionedTemplate = templateVersionRegistry[name][versionKey];
    if (versionedTemplate) {
      return versionedTemplate;
    }
    // If specific version not found, fall back to latest
    return templateVersionRegistry[name]["latest"] || null;
  }

  // Otherwise, get the latest version from the main registry
  return templateRegistry[name as TemplateName] || null;
}

/**
 * Custom hook to get a template component
 * @param templateName The name of the template
 * @param version Optional version of the template
 * @returns The template component or null if not found
 */
export function useTemplate(templateName: string, version?: string) {
  return getTemplate(templateName, version);
}

/**
 * Get all available template names
 * @returns Array of template names
 */
export function getAvailableTemplates(): TemplateName[] {
  return Object.keys(templateRegistry) as TemplateName[];
}

/**
 * Get all available versions for a template
 * @param templateName The name of the template
 * @returns Array of version names or empty array if template not found
 */
export function getAvailableVersions(templateName: string): TemplateVersion[] {
  const name = templateName as TemplateName;
  if (!templateVersionRegistry[name]) {
    return [];
  }

  return Object.keys(templateVersionRegistry[name]) as TemplateVersion[];
}
